import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { colord } from "colord";
import { merge, omit } from "lodash";

import { misc } from "app/themes/colors";
import SelectPatient from "components/custom-filters-list/filters/select-patient.component";
import InputFormField from "components/input-form-field";

import { usePatientNotesDrawer } from "../use-patient-notes-drawer.hook";

export type PatientNotesFormValues = {
  id?: number;
  title?: string;
  description?: string;
  patient: { label: string; value: string } | null;
};

const EMPTY_DEFAULT_VALUES: PatientNotesFormValues = {
  title: "",
  description: "",
  patient: null
};

type PatientNotesFormProps = {
  initialValues?: PatientNotesFormValues;
};

export default function PatientNotesForm({
  initialValues
}: PatientNotesFormProps) {
  const { palette } = useTheme();
  const {
    editMode,
    handleCloseCreate,
    handleCreate,
    isCreating,
    handleUpdate,
    isUpdating,
    handleDelete
  } = usePatientNotesDrawer();

  const defaultValues: PatientNotesFormValues = useMemo(() => {
    return merge({}, EMPTY_DEFAULT_VALUES, initialValues);
  }, [initialValues]);

  const formMethods = useForm<PatientNotesFormValues>({
    mode: "onBlur",
    defaultValues
  });
  const {
    control,
    reset,
    handleSubmit,
    formState: { isValid }
  } = formMethods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = (data: PatientNotesFormValues) => {
    if (data.id && initialValues?.patient?.value !== data?.patient?.value) {
      handleCreate(omit(data, "id"));
      handleDelete({
        id: data.id,
        patient: { id: Number(initialValues?.patient?.value) },
        title: String(initialValues?.title)
      });
    } else if (data.id) {
      handleUpdate(data);
    } else {
      handleCreate(data);
    }
  };
  return (
    <Box
      sx={{ width: "100%" }}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Stack
        sx={{
          width: "100%",
          height: "100%",
          mb: 2,
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <Typography
          variant="h2"
          color="textSecondary"
          sx={{
            color: colord(misc.darkYellow).darken(0.25).toHex()
          }}
        >
          {Boolean(editMode) ? "Edit Note" : "New Note"}
        </Typography>
      </Stack>

      <Grid
        item
        container
        xs={12}
        spacing={2}
        sx={{ mb: 5, width: "100%", pl: 1, pr: 2 }}
      >
        <Grid item xs={12}>
          <InputFormField
            name="title"
            label="Title"
            control={control}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <SelectPatient
            name="patient"
            label="Patient"
            required
            control={control}
            sx={{ ml: 2 }}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                sx={{ pl: 2, background: palette.customTest.bgcolor }}
                placeholder="Enter note details"
                variant="outlined"
                multiline
                fullWidth
                minRows={2}
                maxRows={5}
                {...field}
              />
            )}
          />
        </Grid>
      </Grid>

      <Stack
        sx={{ mt: 4, pr: 3 }}
        justifyContent="flex-end"
        spacing={2}
        direction="row"
      >
        <Button variant="outlined" size="small" onClick={handleCloseCreate}>
          Cancel
        </Button>
        <LoadingButton
          disabled={!isValid}
          loading={isCreating || isUpdating}
          variant="contained"
          size="small"
          type="submit"
        >
          Save
        </LoadingButton>
      </Stack>
    </Box>
  );
}
